import React from "react";
import { ResidentialUserProfileSelectors } from "./ResidentialUserProfileSelectors";
import Button from "views/fetch-design/Button/Button";
import { Col, Form, Row } from "reactstrap";
import FetchDesignSelect, {
  FetchDesignSelectOptionType,
} from "views/fetch-design/FetchDesignSelect/FetchDesignSelect";
import { FetchNotice } from "views/common/cards/FetchNotice";
import { FetchFieldLabel } from "views/common/forms/fields/FetchFieldLabel";
import { FetchToggleSwitch } from "views/common/forms/fields/FetchToggleSwitch/FetchToggleSwitch";
import FetchTermsLink from "views/common/links/FetchTermsLink";
import FetchPolicyLink from "views/common/links/FetchPolicyLink";
import { TermsConditionsPolicySelectors } from "./TermsConditionsPolicySelectors";
import { DeliveryPreferencesSelectors } from "./DeliveryPreferencesSelectors";
import { copy } from "constants/copy";
import { CardLoadingComponent } from "views/components/cards/helpers/CardLoadingComponent";
import LeaveLiabilityWaiver from "views/components/atoms/LeaveLiabilityWaiver";
import { ModalPlain } from "views/fetch-design/ModalPlain/ModalPlain";
import { BEM } from "helpers/BEM.helper";
import { RegistrationLeaveLiabilityWaiverModalSelectors } from "./RegistrationLeaveLiabilityWaiverModalSelectors";
import { ResidentialUserProfileValues } from "domains/user-management/models/ResidentialUserProfile/types/ResidentialUserProfileValues";

const waiverModalClasses = new BEM({
  block: { name: "RegistrationLeaveLiabilityWaiverModal" },
  elements: [
    { name: "accept", extras: ["w-100"] },
    { name: "decline", extras: ["w-100"] },
    { name: "body" },
  ],
});

export type ResidentialUserProfileProps = {
  enabled: boolean;
  isValid: boolean;
  loading: boolean;
  waiverModalIsOpen: boolean;
  marketOptions: FetchDesignSelectOptionType[];
  buildingOptions: FetchDesignSelectOptionType[];
  buildingUnitOptions: FetchDesignSelectOptionType[];
  registrationHelp?: {
    buildingName: string;
    instructions: string;
  };
  showPackageDeliveryPreference?: boolean;
  canToggleDeliveryPreference?: boolean;
  showTCPField?: boolean;
  deliveryPreferenceSetByBuilding: boolean;
  residentTermsAndConditionsUrl?: string;
  residentPrivacyPolicyUrl?: string;
  values: ResidentialUserProfileValues;
  errors: Partial<Record<keyof ResidentialUserProfileValues, string>>;
  onChangeMarket: (option: FetchDesignSelectOptionType | null) => void;
  onChangeBuilding: (option: FetchDesignSelectOptionType | null) => void;
  onChangeBuildingUnit: (option: FetchDesignSelectOptionType | null) => void;
  onChangeDeliveryPreference: (checked: boolean) => void;
  onChangeTermsAccepted: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClickAcceptWaiver: () => void;
  onClickCancelWaiver: () => void;
  handleSubmit: React.FormEventHandler<HTMLFormElement>;
  hideSubmit?: boolean;
};

export const ResidentialUserProfile: React.FC<ResidentialUserProfileProps> = (
  props: ResidentialUserProfileProps,
) => {
  return (
    <Form
      className={ResidentialUserProfileSelectors.block}
      data-cy={ResidentialUserProfileSelectors.block}
      onSubmit={(event) => {
        event.preventDefault();
        props.handleSubmit(event);
      }}
    >
      {props.loading && <CardLoadingComponent display />}
      <ModalPlain
        className={waiverModalClasses.getBlockClassNames()}
        data-cy={RegistrationLeaveLiabilityWaiverModalSelectors.block}
        isOpen={props.waiverModalIsOpen}
        handleClose={props.onClickCancelWaiver}
        title=""
      >
        <LeaveLiabilityWaiver />
        <Row>
          <Col>
            <Button
              color="primary"
              onClick={props.onClickAcceptWaiver}
              className={waiverModalClasses.getElementClassNames("accept")}
              data-cy={RegistrationLeaveLiabilityWaiverModalSelectors.accept}
            >
              Accept
            </Button>
          </Col>
          <Col>
            <Button
              color="secondary"
              onClick={props.onClickCancelWaiver}
              className={waiverModalClasses.getElementClassNames("decline")}
              data-cy={RegistrationLeaveLiabilityWaiverModalSelectors.decline}
            >
              Decline
            </Button>
          </Col>
        </Row>
      </ModalPlain>
      <div data-cy={ResidentialUserProfileSelectors.market}>
        <FetchDesignSelect
          className={ResidentialUserProfileSelectors.market}
          labelText={copy.userType.resident.market}
          name="marketId"
          error={
            props.errors.market ? { message: props.errors.market } : undefined
          }
          onChange={props.onChangeMarket}
          options={props.marketOptions}
          value={props.values.market}
        />
      </div>
      <div data-cy={ResidentialUserProfileSelectors.building}>
        <FetchDesignSelect
          className={ResidentialUserProfileSelectors.building}
          labelText="Community"
          name="buildingId"
          error={
            props.errors.building
              ? { message: props.errors.building }
              : undefined
          }
          onChange={props.onChangeBuilding}
          options={props.buildingOptions}
          value={props.values.building}
        />
      </div>
      {props.registrationHelp && (
        <div
          className="my-2"
          data-cy={ResidentialUserProfileSelectors.registrationInstructions}
        >
          <FetchNotice
            body={props.registrationHelp.instructions}
            icon="notice"
            title={`${props.registrationHelp.buildingName} Registration Instructions`}
            className={ResidentialUserProfileSelectors.registrationInstructions}
          />
        </div>
      )}
      <div data-cy={ResidentialUserProfileSelectors.apartmentNumber}>
        <FetchDesignSelect
          className={ResidentialUserProfileSelectors.apartmentNumber}
          labelText="Unit Number"
          name="buildingUnitId"
          error={
            props.errors.buildingUnit
              ? { message: props.errors.buildingUnit }
              : undefined
          }
          onChange={props.onChangeBuildingUnit}
          options={props.buildingUnitOptions}
          value={props.values.buildingUnit}
        />
      </div>
      {props.showPackageDeliveryPreference && (
        <div
          className={DeliveryPreferencesSelectors.block + " my-2"}
          data-cy={ResidentialUserProfileSelectors.deliveryPreferenceToggle}
        >
          {props.canToggleDeliveryPreference && (
            <div className="d-flex">
              <FetchFieldLabel
                className={
                  DeliveryPreferencesSelectors.label +
                  " fds-emphasis-100 my-auto"
                }
                data-cy={DeliveryPreferencesSelectors.label}
                label="Leave at Door: "
              />
              &nbsp;
              {props.values.leaveAtDoor ? (
                <span className="text-success">Enabled</span>
              ) : (
                <span className="text-danger">Disabled</span>
              )}
            </div>
          )}
          <Row>
            <Col
              className={DeliveryPreferencesSelectors.optionsContainer}
              data-cy={DeliveryPreferencesSelectors.optionsContainer}
            >
              {props.canToggleDeliveryPreference && (
                <FetchToggleSwitch
                  className={DeliveryPreferencesSelectors.toggle}
                  data-cy={DeliveryPreferencesSelectors.toggle}
                  onChange={props.onChangeDeliveryPreference}
                  isChecked={!!props.values.leaveAtDoor}
                />
              )}
              {props.deliveryPreferenceSetByBuilding && (
                <div
                  className={DeliveryPreferencesSelectors.setByBuilding}
                  data-cy={ResidentialUserProfileSelectors.setByBuilding}
                >
                  Your building does not allow Fetch to leave packages at the
                  door.
                </div>
              )}
            </Col>
          </Row>
          <Row>
            <Col
              className={DeliveryPreferencesSelectors.explanation}
              data-cy={
                ResidentialUserProfileSelectors.deliveryPreferenceExplanation
              }
            >
              <strong className="pr-2">Explanation:</strong>
              <span className={"subtle"}>
                {props.values.leaveAtDoor
                  ? "If you're not home upon the first attempt, we will leave your package at your door. You are liable for packages left at your door."
                  : "If you're not home upon the first attempt, a second attempt will be made after 30 minutes. If you are not home upon the second attempt, your package will be returned to the Fetch warehouse."}
              </span>
            </Col>
          </Row>
        </div>
      )}
      {props.showTCPField &&
        !!props.residentTermsAndConditionsUrl &&
        !!props.residentPrivacyPolicyUrl && (
          <div
            className={
              TermsConditionsPolicySelectors.block + " d-flex flex-row"
            }
            data-cy={ResidentialUserProfileSelectors.tcpField}
          >
            <input
              className={TermsConditionsPolicySelectors.checkbox}
              style={{ width: "auto", height: "auto" }}
              data-cy={TermsConditionsPolicySelectors.checkbox}
              type="checkbox"
              name="termsAccepted"
              onChange={props.onChangeTermsAccepted}
              checked={!!props.values.termsAccepted}
            />
            <label
              className={TermsConditionsPolicySelectors.label + " mb-0"}
              data-cy={TermsConditionsPolicySelectors.label}
            >
              I agree to the&nbsp;
              <FetchTermsLink url={props.residentTermsAndConditionsUrl} />
              &nbsp;and&nbsp;
              <FetchPolicyLink url={props.residentPrivacyPolicyUrl} />
            </label>
          </div>
        )}
      {!props.showTCPField &&
        !!props.residentTermsAndConditionsUrl &&
        !!props.residentPrivacyPolicyUrl && (
          <div
            data-cy={ResidentialUserProfileSelectors.tcpLinks}
            className="text-center"
          >
            <FetchTermsLink url={props.residentTermsAndConditionsUrl} />
            &nbsp;|&nbsp;
            <FetchPolicyLink url={props.residentPrivacyPolicyUrl} />
          </div>
        )}
      {!props.hideSubmit && (
        <div className="w-100 text-center mt-2">
          <Button
            type="submit"
            disabled={!props.enabled && !props.isValid}
            className={
              ResidentialUserProfileSelectors.submit + " fds-bg-blue-500"
            }
            data-cy={ResidentialUserProfileSelectors.submit}
          >
            Submit
          </Button>
        </div>
      )}
    </Form>
  );
};
